import { MsalProvider } from '@azure/msal-react';
import { msalInstance, initializeMsal } from './loginMSALAuthProcess';
import LoginMSALConnector from './LoginMSALConnector';
import { cleanupOrphanInteractionLock } from './AuthInteractionLock';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { BluCloudThemeProvider, BluLoader } from '@bludata/components';
import { useEffect, useState } from 'react';
import { logLoginFlow, logError } from './loginLogger';
import EmptyPage from '../utility_objects/EmptyPage';

function LoginMSALProvider() {
  const { themeMode } = useSelector((state: RootState) => state.root);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<Error | null>(null);

  useEffect(() => {
    const initialize = async () => {
      try {
        logLoginFlow('MSAL_PROVIDER_INIT_START');
        await initializeMsal();
        logLoginFlow('MSAL_PROVIDER_INIT_COMPLETE');

        // Pulizia lock orfani prima di rendere attivi i figli
        cleanupOrphanInteractionLock();

        // Barrier: processa la redirect response PRIMA che MsalProvider e figli montino.
        // MSAL v3 handleRedirectPromise è idempotente (cached Promise). L'account viene
        // scritto nella browser cache durante acquireToken(), indipendentemente da chi
        // consuma il risultato. MsalProvider richiamerà la stessa Promise nel suo useEffect.
        if (localStorage.getItem('focus.auth.v2.redirectBarrier') !== '0') {
          try {
            await Promise.race([
              msalInstance.handleRedirectPromise(),
              new Promise((resolve) => setTimeout(resolve, 5000)),
            ]);
          } catch (barrierError) {
            logError('HANDLE_REDIRECT_BARRIER', barrierError);
          }
        }

        setIsInitialized(true);
      } catch (error) {
        logError('MSAL_PROVIDER_INIT', error);
        setInitError(error instanceof Error ? error : new Error(String(error)));
      }
    };

    initialize();
  }, []);

  // Mostra loader mentre MSAL si sta inizializzando
  if (!isInitialized) {
    return (
      <BluCloudThemeProvider themeMode={themeMode}>
        <EmptyPage>
          <BluLoader open />
        </EmptyPage>
      </BluCloudThemeProvider>
    );
  }

  // Mostra errore se l'inizializzazione fallisce
  if (initError) {
    return (
      <BluCloudThemeProvider themeMode={themeMode}>
        <EmptyPage>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <h2>Errore di inizializzazione</h2>
            <p>
              Si è verificato un errore durante l'inizializzazione del sistema
              di autenticazione.
            </p>
            <button onClick={() => window.location.reload()}>Riprova</button>
          </div>
        </EmptyPage>
      </BluCloudThemeProvider>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <BluCloudThemeProvider themeMode={themeMode}>
        <LoginMSALConnector />
      </BluCloudThemeProvider>
    </MsalProvider>
  );
}

export default LoginMSALProvider;
